import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import client from "../lib/api";
import { useMarket } from "../components/Layout";
import { fmtCurrency, fmtNum, marketPill, scoreChipClass, kindLabel } from "../lib/format";
import { toast, Toaster } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../components/ui/dropdown-menu";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import IndexChart from "../components/IndexChart";

const THRESHOLDS = [
  { v: "0.5", label: "≥ 50%" },
  { v: "0.6", label: "≥ 60%" },
  { v: "0.7", label: "≥ 70%" },
  { v: "0.8", label: "≥ 80%" },
];

export default function Home() {
  const market = useMarket();
  const [txs, setTxs] = useState([]);
  const [summary, setSummary] = useState(null);
  const [clusters, setClusters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [asxOpen, setAsxOpen] = useState(false);
  const [threshold, setThreshold] = useState(localStorage.getItem("ie_signal_threshold") || "0.6");
  const [kindFilter, setKindFilter] = useState(localStorage.getItem("ie_kind_filter") || "all");
  const [sortKey, setSortKey] = useState("score");
  const [sortDir, setSortDir] = useState("desc");
  const nav = useNavigate();

  useEffect(() => { localStorage.setItem("ie_signal_threshold", threshold); }, [threshold]);
  useEffect(() => { localStorage.setItem("ie_kind_filter", kindFilter); }, [kindFilter]);

  const load = async () => {
    setLoading(true);
    try {
      const surfacedParams = { market, min_score: parseFloat(threshold), limit: 100 };
      // when threshold >= 0.7, keep the surfaced-only view; below that, include review too
      if (parseFloat(threshold) >= 0.6) surfacedParams.status = "surfaced";
      const [s, t, c] = await Promise.all([
        client.get("/summary"),
        client.get("/transactions", { params: surfacedParams }),
        client.get("/clusters", { params: { market } }),
      ]);
      setSummary(s.data);
      setTxs(t.data);
      setClusters(c.data);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [market, threshold]);

  const [refreshing, setRefreshing] = useState(false);
  const handleRefresh = async () => {
    setRefreshing(true);
    toast.info("Polling SEC + ASX live feeds…");
    try {
      const { data } = await client.post("/admin/refresh");
      toast.success(`Refreshed · SEC +${data.sec_inserted} · ASX +${data.asx?.inserted ?? 0}`);
      load();
    } catch (e) {
      toast.error("Refresh failed: " + (e?.response?.data?.detail || e.message));
    } finally { setRefreshing(false); }
  };

  const visibleTxs = useMemo(() => {
    const filtered = txs.filter(tx => {
      if (kindFilter === "buy") return tx.kind.includes("buy");
      if (kindFilter === "sell") return tx.kind.includes("sell");
      return true;
    });
    const dir = sortDir === "asc" ? 1 : -1;
    const key = sortKey;
    return [...filtered].sort((a, b) => {
      let va = a[key], vb = b[key];
      if (key === "filing_date") { va = new Date(va).getTime(); vb = new Date(vb).getTime(); }
      if (va == null) return 1;
      if (vb == null) return -1;
      return va > vb ? dir : va < vb ? -dir : 0;
    });
  }, [txs, kindFilter, sortKey, sortDir]);

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir(sortDir === "asc" ? "desc" : "asc");
    else { setSortKey(key); setSortDir("desc"); }
  };
  const sortIndicator = (key) => sortKey === key ? (sortDir === "asc" ? " ↑" : " ↓") : "";

  const runSecIngest = async () => {
    toast.info("Polling SEC EDGAR…");
    try {
      const { data } = await client.post("/ingest/sec", null, { params: { count: 15 } });
      toast.success(`Ingested ${data.inserted} new SEC filings`);
      load();
    } catch (e) {
      toast.error("Ingestion failed: " + (e?.response?.data?.detail || e.message));
    }
  };

  const runAsxPoll = async () => {
    toast.info("Polling ASX announcements…");
    try {
      const { data } = await client.post("/ingest/asx-poll");
      const msg = `ASX poll · fetched ${data.fetched} · matched ${data.matched} · inserted ${data.inserted}`;
      if (data.inserted > 0) toast.success(msg);
      else if (data.matched > 0) toast.warning(`${msg} · ${data.failed} failed extractions`);
      else if (data.fetched === 0) toast.warning("ASX endpoint returned 0 filings (may be blocked or after hours)");
      else toast.info(msg);
      load();
    } catch (e) {
      toast.error("ASX poll failed: " + (e?.response?.data?.detail || e.message));
    }
  };

  const sendDigest = async () => {
    toast.info("Sending digest email…");
    try {
      const { data } = await client.post("/digest/send");
      if (data.sent) toast.success(`Digest sent · id ${data.id?.slice(0, 8)}…`);
      else if (data.stubbed) toast.warning("RESEND_API_KEY not configured — email stubbed to logs");
      else toast.error("Send failed: " + (data.error || "unknown"));
    } catch (e) {
      toast.error("Digest failed: " + (e?.response?.data?.detail || e.message));
    }
  };

  return (
    <div data-testid="home-page">
      <Toaster theme="dark" position="top-right" />

      <IndexChart market={market} />

      {clusters.length > 0 && (
        <ClusterHero clusters={clusters} onNav={(id) => nav(`/tx/${id}`)} />
      )}

      <div className="grid grid-cols-4 gap-4 mb-8" data-testid="stats-grid">
        <Stat label="TOTAL FILINGS" value={fmtNum(summary?.total)} sub="across ASX + US" />
        <Stat label="SURFACED ≥ 60" value={fmtNum(summary?.surfaced)} valueGrad sub="high conviction" subColor="var(--score-high)" />
        <Stat label="REVIEW QUEUE" value={fmtNum(summary?.review_pending)} valueColor="var(--lemon)" sub="pending your call" clickable onClick={() => nav("/review")} />
        <Stat label="HIGHEST SCORE" value={txs[0] ? Math.round(txs[0].score * 100) : "—"} valueGrad sub={txs[0] ? `${txs[0].ticker} · ${txs[0].insider_name.split(" ").slice(-1)[0]}` : "—"} />
      </div>

      <div className="flex items-center justify-between mb-4 px-1">
        <div className="flex items-center gap-3 flex-wrap">
          <h2 className="text-[18px] font-semibold tracking-tight" data-testid="home-title">Today&apos;s high-conviction signals</h2>
          <Select value={threshold} onValueChange={setThreshold}>
            <SelectTrigger data-testid="threshold-selector" className="mono w-28 h-8 text-[11px] bg-black/25 border-white/[0.08] rounded-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="glass rounded-2xl border-white/[0.06]">
              {THRESHOLDS.map(t => (
                <SelectItem key={t.v} value={t.v} data-testid={`threshold-${t.v}`} className="mono text-[11px] rounded-lg">{t.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="flex items-center gap-1 p-1 rounded-full bg-black/25 border border-white/[0.05]" data-testid="kind-filter">
            {[{v:"all",l:"ALL"},{v:"buy",l:"BUYS"},{v:"sell",l:"SELLS"}].map(o => (
              <button
                key={o.v}
                onClick={() => setKindFilter(o.v)}
                data-testid={`kind-${o.v}`}
                className={`mono text-[10px] px-3 py-1.5 rounded-full font-semibold transition-colors ${kindFilter === o.v ? "text-[#0f1420]" : "text-[color:var(--text-secondary)] hover:text-[color:var(--text-primary)]"}`}
                style={kindFilter === o.v ? { background: o.v === "buy" ? "var(--score-high)" : o.v === "sell" ? "var(--pink)" : "linear-gradient(135deg, var(--violet), var(--cyan))" } : {}}
              >{o.l}</button>
            ))}
          </div>
          <FreshnessBadge summary={summary} onRefresh={handleRefresh} refreshing={refreshing} />
          {parseFloat(threshold) < 0.6 && (
            <span className="mono text-[10px] text-[color:var(--lemon)]" data-testid="threshold-hint">includes review-state items</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button onClick={sendDigest} data-testid="digest-btn" className="btn-ghost">Send digest</button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid="ingest-menu-btn" className="btn-primary">Ingest ▾</button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="glass rounded-2xl p-1 mt-2 min-w-[240px]">
              <DropdownMenuItem onClick={runSecIngest} data-testid="ingest-sec-item" className="mono text-xs cursor-pointer rounded-lg px-3 py-2">Poll SEC EDGAR (Form 4)</DropdownMenuItem>
              <DropdownMenuItem onClick={runAsxPoll} data-testid="ingest-asx-poll-item" className="mono text-xs cursor-pointer rounded-lg px-3 py-2">Poll ASX announcements</DropdownMenuItem>
              <DropdownMenuItem onClick={() => setAsxOpen(true)} data-testid="ingest-asx-item" className="mono text-xs cursor-pointer rounded-lg px-3 py-2">Extract ASX Appendix 3Y (manual)…</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <div className="glass overflow-hidden" data-testid="tx-table">
        <div className="grid grid-cols-[80px_100px_1fr_1fr_130px_130px_140px_110px_90px] data-header">
          <div>MARKET</div><div>TICKER</div><div>INSIDER</div><div>COMPANY</div>
          <div className="text-right cursor-pointer hover:text-[color:var(--cyan)]" onClick={() => toggleSort("shares")} data-testid="sort-shares">SHARES{sortIndicator("shares")}</div>
          <div className="text-right cursor-pointer hover:text-[color:var(--cyan)]" onClick={() => toggleSort("value")} data-testid="sort-value">VALUE{sortIndicator("value")}</div>
          <div>KIND</div>
          <div className="cursor-pointer hover:text-[color:var(--cyan)]" onClick={() => toggleSort("filing_date")} data-testid="sort-date">DATE{sortIndicator("filing_date")}</div>
          <div className="text-right cursor-pointer hover:text-[color:var(--cyan)]" onClick={() => toggleSort("score")} data-testid="sort-score">SCORE{sortIndicator("score")}</div>
        </div>
        {loading && <div className="data-cell mono text-[color:var(--text-tertiary)] terminal-loader">Loading data</div>}
        {!loading && visibleTxs.length === 0 && <div className="data-cell mono text-[color:var(--text-tertiary)]">No signals match the current filters.</div>}
        {visibleTxs.map((tx) => (
          <div
            key={tx.id}
            data-testid={`tx-row-${tx.ticker}`}
            onClick={() => nav(`/tx/${tx.id}`)}
            className="grid grid-cols-[80px_100px_1fr_1fr_130px_130px_140px_110px_90px] border-t border-white/[0.04] items-center hover:bg-white/[0.03] cursor-pointer"
          >
            <div className="data-cell"><span className={marketPill(tx.market)}>{tx.market}</span></div>
            <div className="data-cell mono font-semibold text-[color:var(--cyan)]">{tx.ticker}</div>
            <div className="data-cell text-sm">
              <div className="font-medium">{tx.insider_name}</div>
              <div className="text-[12px] text-[color:var(--text-tertiary)]">{tx.insider_role}</div>
            </div>
            <div className="data-cell text-sm truncate text-[color:var(--text-secondary)]">{tx.company}</div>
            <div className="data-cell mono text-right">{fmtNum(tx.shares)}</div>
            <div className="data-cell mono text-right font-medium">{fmtCurrency(tx.value, tx.currency)}</div>
            <div className="data-cell text-[11px] mono uppercase tracking-wide" style={{ color: tx.kind.includes("buy") ? "var(--score-high)" : tx.kind.includes("sell") ? "var(--pink)" : "var(--text-secondary)" }}>{kindLabel(tx.kind)}</div>
            <div className="data-cell mono text-[12px] text-[color:var(--text-tertiary)]">{tx.filing_date}</div>
            <div className="data-cell text-right"><span className={scoreChipClass(tx.score)}>{(tx.score * 100).toFixed(0)}</span></div>
          </div>
        ))}
      </div>

      <AsxIngestDialog open={asxOpen} onOpenChange={setAsxOpen} onDone={load} />
    </div>
  );
}

function ClusterHero({ clusters, onNav }) {
  const c = clusters[0];
  return (
    <div className="mb-7 relative overflow-hidden rounded-[28px] border border-[rgba(139,125,255,0.32)] p-6 md:p-7" style={{ background: "linear-gradient(135deg, rgba(139,125,255,0.18), rgba(77,215,243,0.06))", backdropFilter: "blur(24px)" }} data-testid="cluster-alert">
      <div className="absolute -top-40 -right-10 w-[400px] h-[400px] rounded-full pointer-events-none" style={{ background: "radial-gradient(circle, rgba(139,125,255,0.35), transparent 70%)", filter: "blur(60px)" }} />
      <div className="relative grid grid-cols-[auto_1fr_auto_auto] items-center gap-6">
        <div>
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[11px] font-semibold tracking-wide" style={{ background: "rgba(139,125,255,0.24)", border: "1px solid rgba(139,125,255,0.4)", color: "#e0dbff" }}>
            <span className="dot-glow" />CLUSTER ALERT
          </div>
          <div className="text-[17px] font-semibold mt-2.5" data-testid={`cluster-${c.ticker}`} onClick={() => onNav(c.tx_ids[0])}>
            <span className="hover:underline cursor-pointer">{c.company}</span> · <span className="mono text-[color:var(--cyan)]">{c.ticker}</span>
          </div>
          <div className="text-[13px] text-[color:var(--text-secondary)] mt-1">
            {c.insider_count} insiders bought within {daysBetween(c.first_date, c.last_date)} days · {c.insiders.slice(0, 3).join(", ")}
          </div>
        </div>
        <div />
        <div className="text-right">
          <div className="mono text-[28px] font-semibold text-[color:var(--cyan)] tracking-tight">{fmtCurrency(c.total_value, c.currency)}</div>
          <div className="text-[11px] text-[color:var(--text-tertiary)] mt-0.5">total value</div>
        </div>
        <div className="mono text-[12px] text-[color:var(--text-tertiary)] text-right leading-relaxed">
          {c.first_date}<br />→ {c.last_date}
        </div>
      </div>
      {clusters.length > 1 && (
        <div className="mt-4 pt-4 border-t border-white/[0.06] flex gap-3 flex-wrap">
          {clusters.slice(1, 4).map((extra) => (
            <div key={extra.ticker} onClick={() => onNav(extra.tx_ids[0])} className="cursor-pointer flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/[0.04] hover:bg-white/[0.08] border border-white/[0.06]" data-testid={`cluster-${extra.ticker}`}>
              <span className={marketPill(extra.market)}>{extra.market}</span>
              <span className="mono font-semibold text-[color:var(--cyan)] text-[13px]">{extra.ticker}</span>
              <span className="mono text-[11px] text-[color:var(--text-secondary)]">{extra.insider_count} ins.</span>
              <span className="mono text-[11px] text-[color:var(--text-tertiary)]">{fmtCurrency(extra.total_value, extra.currency)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function daysBetween(a, b) {
  try {
    return Math.abs(Math.round((new Date(b) - new Date(a)) / 86400000));
  } catch { return "—"; }
}

function AsxIngestDialog({ open, onOpenChange, onDone }) {
  const [url, setUrl] = useState("");
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async () => {
    setBusy(true);
    setResult(null);
    try {
      const body = url ? { pdf_url: url } : { text };
      const { data } = await client.post("/ingest/asx", body);
      setResult(data);
      if (data.stored) {
        toast.success(`Stored · ${data.kind} · score ${(data.score * 100).toFixed(0)} · ${data.method}`);
        onDone();
      } else {
        toast.warning(`Extraction low-confidence (${(data.confidence * 100).toFixed(0)}%) · ${data.method}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="glass rounded-3xl max-w-2xl border-white/[0.06]">
        <DialogHeader><DialogTitle className="text-[15px] font-semibold">ASX Appendix 3Y · Extract</DialogTitle></DialogHeader>
        <div className="space-y-4 py-2">
          <div>
            <label className="block text-[11px] uppercase tracking-wider text-[color:var(--text-tertiary)] mb-2 font-medium">PDF URL (asx.com.au)</label>
            <input
              data-testid="asx-url-input"
              type="url"
              placeholder="https://www.asx.com.au/asxpdf/2025/..."
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              className="mono w-full bg-black/25 border border-white/[0.08] rounded-xl px-4 py-2.5 text-xs outline-none focus:border-[color:var(--cyan)]"
            />
          </div>
          <div className="text-[10px] uppercase tracking-wider text-[color:var(--text-tertiary)] text-center">— or paste raw text —</div>
          <textarea
            data-testid="asx-text-input"
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Name of Director: John Doe&#10;Name of entity: ACME Ltd&#10;ASX Code: ACM&#10;Nature of change: on-market buy&#10;No. of securities: 10,000&#10;Consideration: $50,000"
            rows={6}
            className="mono w-full bg-black/25 border border-white/[0.08] rounded-xl px-4 py-3 text-xs outline-none focus:border-[color:var(--cyan)]"
          />
          {result && (
            <div className="mono text-[11px] rounded-xl border border-white/[0.06] p-3 bg-black/25" data-testid="asx-result">
              <div>method: <span className="text-[color:var(--cyan)]">{result.method}</span></div>
              <div>confidence: {(result.confidence * 100).toFixed(0)}%</div>
              {result.stored && <div className="text-[color:var(--score-high)]">stored id: {result.id?.slice(0, 8)}… · score {(result.score * 100).toFixed(0)}</div>}
              {!result.stored && <div className="text-[color:var(--pink)]">Not stored — refine input or use PDF URL.</div>}
            </div>
          )}
        </div>
        <DialogFooter>
          <button onClick={() => onOpenChange(false)} className="btn-ghost">Close</button>
          <button data-testid="asx-submit" disabled={busy || (!url && !text.trim())} onClick={submit} className="btn-primary">
            {busy ? "Extracting…" : "Extract & Store"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function FreshnessBadge({ summary, onRefresh, refreshing }) {
  if (!summary) return null;
  const iso = summary.latest_ingest;
  let ageMs = null, staleHours = null;
  if (iso) {
    ageMs = Date.now() - new Date(iso).getTime();
    staleHours = ageMs / 3_600_000;
  }
  const stale = staleHours == null || staleHours > 24;
  const label = ageMs == null ? "never" :
    ageMs < 60_000 ? "just now" :
    ageMs < 3_600_000 ? `${Math.floor(ageMs/60_000)}m ago` :
    ageMs < 86_400_000 ? `${Math.floor(ageMs/3_600_000)}h ago` :
    `${Math.floor(ageMs/86_400_000)}d ago`;
  const color = stale ? "var(--lemon)" : "var(--score-high)";
  return (
    <div className="flex items-center gap-2" data-testid="freshness-badge">
      <span className="mono text-[10px] uppercase tracking-wider px-2 py-1 rounded-full border" style={{ color, borderColor: `${color}55`, background: `${color}14` }}>
        <span className="inline-block w-1.5 h-1.5 rounded-full mr-1.5 align-middle" style={{ background: color, boxShadow: `0 0 8px ${color}` }} />
        last ingest: {label}
      </span>
      <button onClick={onRefresh} disabled={refreshing} data-testid="refresh-btn" className="mono text-[10px] uppercase tracking-wider px-2.5 py-1 rounded-full border border-white/[0.10] text-[color:var(--cyan)] hover:border-[color:var(--cyan)]/50 disabled:opacity-40">
        {refreshing ? "…" : "Refresh"}
      </button>
    </div>
  );
}

function Stat({ label, value, valueColor, valueGrad, sub, subColor, clickable, onClick }) {
  return (
    <div className={`glass p-5 relative overflow-hidden ${clickable ? "cursor-pointer hover:bg-white/[0.04]" : ""}`} onClick={onClick} data-testid={`stat-${label.split(" ")[0].toLowerCase()}`}>
      <div className="absolute top-0 left-0 right-0 h-px" style={{ background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.08), transparent)" }} />
      <div className="text-[11px] uppercase tracking-wider text-[color:var(--text-secondary)] font-medium mb-3">{label}</div>
      <div className={`mono text-[32px] font-semibold tracking-tight leading-none ${valueGrad ? "grad-text" : ""}`} style={{ color: !valueGrad && valueColor ? valueColor : undefined }}>{value ?? "—"}</div>
      {sub && <div className="text-[11px] mt-2 mono" style={{ color: subColor || "var(--text-tertiary)" }}>{sub}</div>}
    </div>
  );
}
